/**
 * UI 2026 refresh flag.
 *
 * The visual refresh ships as a set of CSS custom-property overrides scoped
 * under `:root.ui-2026` (see ui2026.css). Nothing in the original token file
 * is edited, so with this class absent the app renders exactly as it did
 * before the refresh landed.
 *
 * Resolution order (first match wins):
 *   1. URL param  ?ui=classic | ?ui=2026   - per-session escape hatch
 *   2. user pref  theme.ui2026             - applied later via syncWithUser
 *   3. localStorage `app-ui-2026`          - remembers the last explicit choice
 *   4. VITE_UI_2026 build flag             - the deploy-wide default
 *
 * To roll the refresh back globally: set VITE_UI_2026=false and redeploy.
 * To roll it back for one user: append ?ui=classic to any URL.
 */

const STORAGE_KEY = 'app-ui-2026';
const CLASS_NAME = 'ui-2026';

/**
 * Session override from the query string, captured once at module load.
 * React Router drops the param on the first navigation, so we cannot re-read
 * `location.search` later - it has to be latched here.
 *
 * `null` means "no override, fall through to the other sources".
 */
const urlOverride: boolean | null = (() => {
  try {
    const value = new URLSearchParams(window.location.search).get('ui');
    if (value === 'classic') return false;
    if (value === '2026') return true;
  } catch {
    // Non-browser env (tests, SSR) - no override.
  }
  return null;
})();

/** True when ?ui= pinned the flag for this session; user prefs must not override it. */
export function hasSessionOverride(): boolean {
  return urlOverride !== null;
}

function envDefault(): boolean {
  try {
    return import.meta.env.VITE_UI_2026 === 'true';
  } catch {
    return false;
  }
}

export function resolveInitialUi2026(): boolean {
  if (urlOverride !== null) return urlOverride;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'true') return true;
    if (saved === 'false') return false;
  } catch {
    // localStorage unavailable - fall through to the build default.
  }
  return envDefault();
}

/**
 * Toggle the root class. `persist` is false during boot so that simply loading
 * the app with the build default does not write a sticky localStorage value the
 * user never asked for.
 */
export function applyUi2026Class(on: boolean, persist = true): void {
  document.documentElement.classList.toggle(CLASS_NAME, on);
  if (!persist) return;
  try {
    localStorage.setItem(STORAGE_KEY, String(on));
  } catch {
    // Ignore quota/private-mode failures - the class is already applied.
  }
}
